import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trash2, Plus, Minus, ShoppingBag, CreditCard, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { PaymentForm, CreditCard as SquareCreditCard } from 'react-square-web-payments-sdk';

export const Cart = () => {
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [tipPercentage, setTipPercentage] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [orderingInfo, setOrderingInfo] = useState(null);
  const [squareConfig, setSquareConfig] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [addressForm, setAddressForm] = useState({ street: "", city: "", state: "", zip_code: "" });
  const [deliveryQuote, setDeliveryQuote] = useState(null); // { available, delivery_fee, message }
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [foodTruckSchedule, setFoodTruckSchedule] = useState([]);
  const [selectedStopId, setSelectedStopId] = useState("");

  // Detect food-truck cart from the marker we set in FoodTruck.jsx
  const isFoodTruckOrder = cart.length > 0 && cart.every(
    (it) => it.fulfillment_type === "food_truck_pickup"
  );

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchSquareConfig = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/payments/config`);
        setSquareConfig(data);
      } catch (error) {
        console.error("Failed to fetch Square config:", error);
      }
    };

    const fetchSchedulerInfo = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/scheduler/info`);
        setDeliveryDate(data.delivery_date);
        setOrderingInfo(data);
      } catch (error) {
        console.error("Failed to fetch scheduler info:", error);
      }
    };

    const fetchFoodTruckSchedule = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/food-truck/schedule?upcoming_only=true`);
        setFoodTruckSchedule(data);
      } catch (error) {
        console.error("Failed to fetch food truck schedule:", error);
      }
    };

    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    fetchSchedulerInfo();
    fetchSquareConfig();
    fetchFoodTruckSchedule();
  }, [BACKEND_URL]);

  const updateCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
  };

  const updateQuantity = (itemId, change) => {
    const newCart = cart.map(item => {
      if (item.id === itemId) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean);
    
    updateCart(newCart);
    if (change < 0 && !newCart.find(item => item.id === itemId)) {
      toast.success("Item removed from cart");
    }
  };

  const removeItem = (itemId) => {
    const newCart = cart.filter(item => item.id !== itemId);
    updateCart(newCart);
    toast.success("Item removed from cart");
  };

  // Debounced delivery-fee quote whenever the address is fully filled in
  useEffect(() => {
    const { street, city, state, zip_code } = addressForm;
    const ready =
      street.trim().length > 2 &&
      city.trim().length > 1 &&
      state.trim().length > 0 &&
      /^\d{5}$/.test(zip_code.trim());

    if (!ready) {
      setDeliveryQuote(null);
      return;
    }

    setQuoteLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/delivery-zones/quote`, {
          street: street.trim(),
          city: city.trim(),
          state: state.trim(),
          zip_code: zip_code.trim(),
        });
        setDeliveryQuote(data);
      } catch (err) {
        console.error("Delivery quote failed:", err);
        setDeliveryQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [addressForm, BACKEND_URL]);

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.0825; // 8.25% Fort Worth, TX sales tax

  // Food truck = no delivery fee. Meal prep = real backend-calculated fee when address is valid.
  const deliveryFee = isFoodTruckOrder
    ? 0
    : (deliveryQuote && deliveryQuote.available ? Number(deliveryQuote.delivery_fee) : 0);
  
  // Calculate tip
  let tipAmount = 0;
  if (tipPercentage > 0) {
    tipAmount = subtotal * (tipPercentage / 100);
  } else if (customTip) {
    tipAmount = parseFloat(customTip) || 0;
  }
  
  const total = subtotal + tax + deliveryFee + tipAmount;

  const handleCheckout = async (tokenResult, verifiedBuyer) => {
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      return;
    }

    // For meal-prep delivery we need an in-area address.
    // For food-truck pre-orders we need a chosen stop instead.
    if (isFoodTruckOrder) {
      if (!selectedStopId) {
        toast.error("Please choose which food-truck stop to pick up at.");
        return;
      }
    } else {
      if (!deliveryQuote || !deliveryQuote.available) {
        toast.error(
          deliveryQuote?.message ||
            "Please enter a complete delivery address so we can calculate the fee."
        );
        return;
      }
    }

    // Square's tokenize callback gives us a full result object — extract the
    // token string. For manual payments (Zelle/CashApp) tokenResult is null.
    let sourceId = null;
    if (tokenResult) {
      if (typeof tokenResult === "string") {
        sourceId = tokenResult;
      } else if (tokenResult.status === "OK" && tokenResult.token) {
        sourceId = tokenResult.token;
      } else {
        const errMsg =
          tokenResult.errors?.[0]?.message ||
          `Card tokenization failed (status: ${tokenResult.status || "UNKNOWN"})`;
        toast.error(errMsg);
        return;
      }
    }

    const form = document.getElementById('checkout-form');
    const formData = new FormData(form);
    
    setIsProcessingPayment(true);

    // Look up selected food-truck stop (if applicable)
    const selectedStop = isFoodTruckOrder
      ? foodTruckSchedule.find((s) => s.id === selectedStopId)
      : null;

    const orderData = {
      items: cart.map(item => ({
        menu_item_id: item.menu_item_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      })),
      fulfillment_type: isFoodTruckOrder ? 'food_truck_pickup' : 'delivery',
      delivery_address: isFoodTruckOrder ? null : {
        street: formData.get('street'),
        city: formData.get('city'),
        state: formData.get('state'),
        zip_code: formData.get('zip_code'),
        delivery_instructions: formData.get('delivery_instructions') || ''
      },
      delivery_time_slot: isFoodTruckOrder
        ? `${selectedStop?.location || 'Food Truck'} • ${selectedStop?.start_time || ''} - ${selectedStop?.end_time || ''}`
        : formData.get('delivery_time_slot'),
      customer_name: formData.get('name'),
      customer_email: formData.get('email'),
      customer_phone: formData.get('phone'),
      payment_method: paymentMethod || 'Square',
      scheduled_date: isFoodTruckOrder
        ? selectedStop?.event_date
        : (deliveryDate || formData.get('scheduled_date') || new Date(Date.now() + 86400000).toISOString()),
      notes: isFoodTruckOrder
        ? `Food truck pre-order — pickup at ${selectedStop?.location}${formData.get('notes') ? ` • ${formData.get('notes')}` : ''}`
        : (formData.get('notes') || ''),
      tip_amount: tipAmount
    };

    try {
      // If Credit Card selected, process payment with Square
      if (paymentMethod === 'Credit Card' && sourceId) {
        const paymentResponse = await axios.post(`${BACKEND_URL}/api/payments/create-payment`, {
          source_id: sourceId,
          amount: Math.round(total * 100),
          currency: 'USD',
          customer_name: orderData.customer_name,
          customer_email: orderData.customer_email,
          customer_phone: orderData.customer_phone,
          items: orderData.items,
          delivery_address: orderData.delivery_address,
          delivery_time_slot: orderData.delivery_time_slot,
          scheduled_date: orderData.scheduled_date,
          tip_amount: tipAmount
        });

        if (paymentResponse.data.status === 'COMPLETED') {
          orderData.payment_id = paymentResponse.data.payment_id;
        }
      }
      
      // Create order
      const orderResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, orderData, { withCredentials: true });
      
      toast.success(`Order ${orderResponse.data.order_number} placed successfully!`);
      updateCart([]);
      localStorage.removeItem("food_truck_cart");
      setShowCheckout(false);
      setTimeout(() => { window.location.href = "/my-orders"; }, 2000);
      
    } catch (error) {
      console.error('Payment/Order error:', error);
      let errorMsg = "Order failed. Please try again.";

      // Handle different error formats
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // FastAPI/Pydantic validation errors → show the first message
          errorMsg = detail[0]?.msg || "Validation error";
        } else if (detail?.msg) {
          errorMsg = detail.msg;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(errorMsg);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleManualPaymentCheckout = async (e) => {
    e.preventDefault();
    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      return;
    }
    if (isFoodTruckOrder) {
      if (!selectedStopId) {
        toast.error("Please choose which food-truck stop to pick up at.");
        return;
      }
    } else if (!deliveryQuote || !deliveryQuote.available) {
      toast.error(
        deliveryQuote?.message ||
          "Please enter a complete delivery address so we can calculate the fee."
      );
      return;
    }
    await handleCheckout(null, null);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-4xl mx-auto text-center py-20">
          <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
          <p className="text-xl text-gray-600 mb-8">
            Start adding some delicious meals to your cart!
          </p>
          <Link to="/meal-prep">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
              Browse Menu
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
          <p className="text-gray-600">
            {cart.reduce((sum, item) => sum + item.quantity, 0)} items in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{item.name}</h3>
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 mt-1">
                            {item.category}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(item.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-2">{item.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center bg-stone-100 rounded-lg p-1 border-2 border-stone-200">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateQuantity(item.id, -1)}
                            className="hover:bg-stone-200"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="text-lg font-semibold px-4">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateQuantity(item.id, 1)}
                            className="hover:bg-stone-200"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <span className="text-2xl font-bold text-emerald-600">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-28 border-2 border-emerald-200">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="text-2xl">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">Tax (8.25%)</span>
                    <span className="font-semibold">${tax.toFixed(2)}</span>
                  </div>
                  {!isFoodTruckOrder && (
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span className="font-semibold" data-testid="delivery-fee-display">
                      {showCheckout && quoteLoading ? (
                        <span className="text-sm text-gray-500">Calculating…</span>
                      ) : showCheckout && deliveryQuote && deliveryQuote.available ? (
                        `$${deliveryFee.toFixed(2)}`
                      ) : showCheckout && deliveryQuote && !deliveryQuote.available ? (
                        <span className="text-sm text-red-600">Outside delivery area</span>
                      ) : showCheckout ? (
                        <span className="text-sm text-gray-400">Enter address</span>
                      ) : (
                        <span className="text-sm text-gray-400">$5–$10 (calculated at checkout)</span>
                      )}
                    </span>
                  </div>
                  )}
                  {isFoodTruckOrder && (
                    <div className="flex justify-between text-lg text-emerald-600">
                      <span>Pickup</span>
                      <span className="font-semibold text-sm">Free at the truck</span>
                    </div>
                  )}
                  {tipAmount > 0 && (
                    <div className="flex justify-between text-lg text-emerald-600">
                      <span>Tip</span>
                      <span className="font-semibold">${tipAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-emerald-600">${total.toFixed(2)}</span>
                  </div>
                </div>

                {!showCheckout ? (
                  <div className="space-y-3">
                    {orderingInfo && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-1">
                        <p className="text-sm font-semibold text-emerald-800">Delivery Information</p>
                        <p className="text-xs text-emerald-700">📅 {orderingInfo.delivery_date_formatted}</p>
                        <p className="text-xs text-emerald-700">🕐 Sunday 12pm-8pm</p>
                      </div>
                    )}
                    <Button
                      onClick={() => setShowCheckout(true)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
                    >
                      <CreditCard className="mr-2 h-5 w-5" />
                      Proceed to Checkout
                    </Button>
                    <Link to="/meal-prep">
                      <Button variant="outline" className="w-full py-6 text-lg border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                        Continue Shopping
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <form id="checkout-form" className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input id="name" name="name" placeholder="John Doe" required className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" name="email" type="email" placeholder="john@example.com" required className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input id="phone" name="phone" type="tel" placeholder="(555) 123-4567" required className="mt-1" />
                      </div>

                      {isFoodTruckOrder ? (
                        <div className="pt-3 border-t" data-testid="food-truck-stop-picker">
                          <Label className="text-base font-semibold">Pick-Up Location</Label>
                          <p className="text-xs text-gray-500 mt-1 mb-2">
                            Choose which food-truck stop you'll pick up at.
                          </p>
                          {foodTruckSchedule.length === 0 ? (
                            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                              No upcoming stops yet. Please call <a href="tel:+16824023734" className="underline font-medium">(682) 402-3734</a> to pre-order.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {foodTruckSchedule.map((stop) => {
                                const dt = new Date(stop.event_date);
                                const checked = selectedStopId === stop.id;
                                return (
                                  <label
                                    key={stop.id}
                                    data-testid={`stop-option-${stop.id}`}
                                    className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                                      checked ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-emerald-300"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="food_truck_stop"
                                      value={stop.id}
                                      checked={checked}
                                      onChange={() => setSelectedStopId(stop.id)}
                                      className="mt-1"
                                    />
                                    <div className="flex-1">
                                      <p className="font-semibold text-gray-900">{stop.location}</p>
                                      {stop.address && <p className="text-xs text-gray-500">{stop.address}</p>}
                                      <p className="text-sm text-gray-700 mt-0.5">
                                        {dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                                        {" • "}{stop.start_time} – {stop.end_time}
                                      </p>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                      <>
                      <div className="pt-3 border-t">
                        <Label className="text-base font-semibold">Delivery Address</Label>
                        <Input
                          name="street"
                          placeholder="Street Address"
                          required
                          className="mt-2 mb-2"
                          value={addressForm.street}
                          onChange={(e) => setAddressForm((p) => ({ ...p, street: e.target.value }))}
                        />

                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <Input
                            name="city"
                            placeholder="City"
                            required
                            value={addressForm.city}
                            onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
                          />
                          <Input
                            name="state"
                            placeholder="State"
                            required
                            value={addressForm.state}
                            onChange={(e) => setAddressForm((p) => ({ ...p, state: e.target.value }))}
                          />
                        </div>

                        <Input
                          name="zip_code"
                          placeholder="Zip Code"
                          required
                          className="mb-2"
                          value={addressForm.zip_code}
                          onChange={(e) => setAddressForm((p) => ({ ...p, zip_code: e.target.value }))}
                        />
                        <Input name="delivery_instructions" placeholder="Delivery Instructions (Optional)" />
                      </div>

                      <div>
                        <Label htmlFor="delivery_time_slot">Delivery Time (Sunday)</Label>
                        <select
                          id="delivery_time_slot"
                          name="delivery_time_slot"
                          required
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Select delivery time</option>
                          <option value="12:00 PM - 1:00 PM">12:00 PM - 1:00 PM</option>
                          <option value="1:00 PM - 2:00 PM">1:00 PM - 2:00 PM</option>
                          <option value="2:00 PM - 3:00 PM">2:00 PM - 3:00 PM</option>
                          <option value="3:00 PM - 4:00 PM">3:00 PM - 4:00 PM</option>
                          <option value="4:00 PM - 5:00 PM">4:00 PM - 5:00 PM</option>
                          <option value="5:00 PM - 6:00 PM">5:00 PM - 6:00 PM</option>
                          <option value="6:00 PM - 7:00 PM">6:00 PM - 7:00 PM</option>
                          <option value="7:00 PM - 8:00 PM">7:00 PM - 8:00 PM</option>
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="scheduled_date">Delivery Date (Sunday)</Label>
                        <Input 
                          id="scheduled_date" 
                          name="scheduled_date" 
                          type="date"
                          value={deliveryDate ? new Date(deliveryDate).toISOString().split('T')[0] : ''}
                          min={new Date().toISOString().split('T')[0]}
                          required
                          readOnly
                          className="mt-1 bg-gray-50 cursor-not-allowed" 
                        />
                        <p className="text-xs text-emerald-600 mt-1 font-semibold">
                          {orderingInfo ? `📅 ${orderingInfo.delivery_date_formatted}` : 'Delivery available Sundays only'}
                        </p>
                      </div>
                      </>
                      )}
                      
                      <div className="pt-3 border-t">
                        <Label className="text-base font-semibold mb-2 block">Add Tip for Chef Eric</Label>
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          <Button
                            type="button"
                            variant={tipPercentage === 10 ? "default" : "outline"}
                            className={tipPercentage === 10 ? "bg-emerald-600" : ""}
                            onClick={() => { setTipPercentage(10); setCustomTip(""); }}
                          >
                            10%
                          </Button>
                          <Button
                            type="button"
                            variant={tipPercentage === 15 ? "default" : "outline"}
                            className={tipPercentage === 15 ? "bg-emerald-600" : ""}
                            onClick={() => { setTipPercentage(15); setCustomTip(""); }}
                          >
                            15%
                          </Button>
                          <Button
                            type="button"
                            variant={tipPercentage === 20 ? "default" : "outline"}
                            className={tipPercentage === 20 ? "bg-emerald-600" : ""}
                            onClick={() => { setTipPercentage(20); setCustomTip(""); }}
                          >
                            20%
                          </Button>
                          <Button
                            type="button"
                            variant={customTip ? "default" : "outline"}
                            className={customTip ? "bg-emerald-600" : ""}
                            onClick={() => {
                              setTipPercentage(0);
                              document.getElementById('custom-tip').focus();
                            }}
                          >
                            Custom
                          </Button>
                        </div>
                        {(tipPercentage === 0 || customTip) && (
                          <Input
                            id="custom-tip"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Enter custom tip amount"
                            value={customTip}
                            onChange={(e) => {
                              setCustomTip(e.target.value);
                              setTipPercentage(0);
                            }}
                            className="mt-2"
                          />
                        )}
                      </div>

                      <Separator className="my-4" />

                      <div>
                        <Label htmlFor="payment">Payment Method</Label>
                        <select
                          id="payment"
                          name="payment"
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          required
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">Select payment method</option>
                          <option value="Credit Card">Credit Card (Square)</option>
                          <option value="CashApp">CashApp</option>
                          <option value="Zelle">Zelle</option>
                        </select>
                      </div>

                      {paymentMethod === 'Credit Card' && (
                        <div>
                          <Label className="text-base font-semibold mb-3 block">Credit Card Information</Label>
                          {squareConfig ? (
                            <>
                              <PaymentForm
                                applicationId={squareConfig.application_id}
                                locationId={squareConfig.location_id}
                                cardTokenizeResponseReceived={handleCheckout}
                              >
                                <SquareCreditCard />
                              </PaymentForm>

                              <div
                                data-testid="powered-by-square"
                                className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500"
                              >
                                <Lock className="h-3.5 w-3.5 text-emerald-600" />
                                <span>
                                  Secure payments powered by{" "}
                                  <span className="font-semibold text-gray-700">Square</span>
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="p-6 bg-gray-50 rounded text-center text-gray-500">Loading payment form...</div>
                          )}
                        </div>
                      )}

                      {paymentMethod && paymentMethod !== 'Credit Card' && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Payment via {paymentMethod}:</strong> You'll receive order confirmation with payment instructions.
                          </p>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="notes">Special Instructions (Optional)</Label>
                        <textarea
                          id="notes"
                          name="notes"
                          placeholder="Any dietary restrictions or special requests..."
                          rows="3"
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                    
                    {paymentMethod && paymentMethod !== 'Credit Card' && (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="terms-agreement-checkbox"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 leading-relaxed">
                            I agree to the{" "}
                            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                              Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                              Privacy Policy
                            </Link>
                            .
                          </span>
                        </label>
                      </div>
                    )}

                    {paymentMethod && paymentMethod !== 'Credit Card' && (
                      <Button
                        type="button"
                        onClick={handleManualPaymentCheckout}
                        disabled={isProcessingPayment || !agreedToTerms}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessingPayment ? 'Processing...' : `Place Order - $${total.toFixed(2)}`}
                      </Button>
                    )}

                    {paymentMethod === 'Credit Card' && (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="terms-agreement-checkbox-card"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 leading-relaxed">
                            I agree to the{" "}
                            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                              Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                              Privacy Policy
                            </Link>
                            . Required to complete card payment above.
                          </span>
                        </label>
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCheckout(false)}
                      className="w-full py-6 text-lg"
                    >
                      Back
                    </Button>
                  </form>
                )}

                <div className="pt-4 space-y-2 text-sm text-gray-600">
                  <p className="flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    Sunday delivery 12pm-8pm
                  </p>
                  <p className="flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    Prepared fresh by Chef Eric
                  </p>
                  <p className="flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    Delivery: $5-$10 based on address
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
